import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface Gider {
  id: number;
  tarih: string;
  aciklama: string;
  detay: string;
  tutar: number;
  created_at: string;
}

interface GiderContextType {
  giderler: Gider[];
  addGider: (gider: Omit<Gider, 'id'>) => void;
  updateGider: (id: number, gider: Omit<Gider, 'id'>) => void;
  deleteGider: (id: number) => void;
  getToplamGider: () => number;
  getBuAykiGider: () => number;
  getAylikGider: (ay: number, yil: number) => number;
}

const GiderContext = createContext<GiderContextType | undefined>(undefined);

export const useGider = () => {
  const context = useContext(GiderContext);
  if (!context) {
    throw new Error('useGider must be used within a GiderProvider');
  }
  return context;
};

interface GiderProviderProps {
  children: ReactNode;
}

export const GiderProvider: React.FC<GiderProviderProps> = ({ children }) => {
  const [giderler, setGiderler] = useState<Gider[]>([]);

  const addGider = (yeniGider: Omit<Gider, 'id'>) => {
    const id = Date.now(); // Basit ID üretimi
    const gider: Gider = {
      ...yeniGider,
      id
    };
    setGiderler(prev => [...prev, gider]);
  };

  const updateGider = (id: number, guncelGider: Omit<Gider, 'id'>) => {
    setGiderler(prev => prev.map(gider => 
      gider.id === id ? { ...guncelGider, id } : gider
    ));
  };

  const deleteGider = (id: number) => {
    setGiderler(prev => prev.filter(gider => gider.id !== id));
  };

  const getToplamGider = () => {
    return giderler.reduce((total, gider) => total + gider.tutar, 0);
  };

  const getBuAykiGider = () => {
    const bugun = new Date();
    const buAy = bugun.getMonth() + 1; // getMonth() 0-11 arası döner
    const buYil = bugun.getFullYear();
    return getAylikGider(buAy, buYil);
  };

  const getAylikGider = (ay: number, yil: number) => {
    return giderler
      .filter(gider => {
        const tarih = new Date(gider.tarih);
        return tarih.getMonth() === ay - 1 && tarih.getFullYear() === yil;
      })
      .reduce((total, gider) => total + gider.tutar, 0);
  };

  const value: GiderContextType = {
    giderler,
    addGider,
    updateGider,
    deleteGider,
    getToplamGider,
    getBuAykiGider,
    getAylikGider
  };

  return (
    <GiderContext.Provider value={value}>
      {children}
    </GiderContext.Provider>
  );
};